// "Mis pedidos": el local ve lo que mando, corrige o cancela lo que todavia
// esta pendiente, y consulta el detalle de lo ya confirmado.

import { api } from "../api";
import { navegar } from "../router";
import { formatearPesos } from "../../lib/money";
import { hoyMontevideo, rangoFechas } from "../../lib/fecha";
import { editarPedidoPendiente } from "./clientepedido";
import { abrirDetalleLocal } from "./detallelocal";
import { alLlegarAviso } from "../notificaciones";
import { nombreTurno } from "../../lib/types";
import type { PedidoDeLocal, LineaDeLocal, EstadoPedido } from "../../lib/types";

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}

type Rango = "recientes" | "dia" | "semana" | "mes" | "todo";

const RANGOS: Array<{ valor: Rango; nombre: string }> = [
  { valor: "recientes", nombre: "Recientes" },
  { valor: "dia", nombre: "Día" },
  { valor: "semana", nombre: "Semana" },
  { valor: "mes", nombre: "Mes" },
  { valor: "todo", nombre: "Todo" },
];

/** Cuantos trae "Recientes". Alcanza para ver lo de los ultimos dias. */
const RECIENTES = 15;

function ddmm(iso: string): string {
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
}

/**
 * El dia en que se mando el pedido, en hora de Montevideo.
 *
 * `creado_en` viene en UTC ('YYYY-MM-DD HH:MM:SS'). Se muestra porque un pedido
 * aparece en el filtro por su fecha de ENTREGA o por la de ENVIO, y sin este
 * dato no se entiende por que el pedido "del 28" sigue estando el 27.
 */
function diaDeEnvio(utc: string): string {
  const d = new Date(utc.replace(" ", "T") + "Z");
  if (Number.isNaN(d.getTime())) return utc.slice(0, 10);
  const local = new Date(d.getTime() - 3 * 60 * 60 * 1000);
  return `${String(local.getUTCDate()).padStart(2, "0")}/${String(local.getUTCMonth() + 1).padStart(2, "0")}`;
}

const CHIP: Record<EstadoPedido, { texto: string; clase: string }> = {
  PENDIENTE: { texto: "Pendiente", clase: "bg-amber-100 text-amber-800" },
  CONFIRMADO: { texto: "Confirmado", clase: "bg-green-100 text-green-800" },
  ANULADO: { texto: "Cancelado", clase: "bg-crema-200 text-masa-500" },
};

export async function renderClientePedidos(contenedor: HTMLElement): Promise<void> {
  contenedor.innerHTML = `
    <div class="mb-4">
      <h1 class="titulo-pagina">Mis pedidos</h1>
      <p class="subtitulo">Mientras un pedido este pendiente lo podés corregir o cancelar.</p>
    </div>

    <div class="tarjeta mb-3 flex flex-wrap items-center justify-between gap-2 py-2">
      <div id="rangos" class="flex flex-wrap gap-1">
        ${RANGOS.map(
          (r) =>
            `<button data-rango="${r.valor}" class="btn-sec px-3 py-2 text-sm rango-btn">${r.nombre}</button>`,
        ).join("")}
      </div>
      <span id="periodo" class="text-xs text-masa-400"></span>
    </div>

    <div id="lista" class="space-y-3"><div class="tarjeta text-center text-masa-400">Cargando...</div></div>
    <dialog id="dlg"></dialog>`;

  const lista = contenedor.querySelector("#lista") as HTMLElement;
  const dlg = contenedor.querySelector("#dlg") as HTMLDialogElement;
  const elPeriodo = contenedor.querySelector("#periodo") as HTMLElement;

  // Arranca en "Recientes": lo habitual es entrar a mirar (o corregir) lo que
  // se acaba de mandar, y eso no depende de ninguna fecha. Los rangos por fecha
  // son para buscar algo puntual hacia atras.
  let rango: Rango = "recientes";
  // Fuera de cargar() para que el manejador de clics pueda mirar el pedido
  // (necesita su local para reabrirlo en la pantalla de pedido).
  let pedidos: PedidoDeLocal[] = [];

  function pintarRangos(): void {
    contenedor.querySelectorAll<HTMLElement>(".rango-btn").forEach((b) => {
      b.classList.toggle("turno-activo", b.dataset.rango === rango);
    });
  }

  async function cargar(): Promise<void> {
    pintarRangos();

    // "Recientes" y "Todo" no llevan fechas; los otros filtran por ENTREGA.
    const porFecha = rango !== "recientes" && rango !== "todo";
    const [desde, hasta] = porFecha
      ? rangoFechas(hoyMontevideo(), rango as "dia" | "semana" | "mes")
      : ["", ""];

    elPeriodo.textContent = porFecha
      ? `entrega ${desde === hasta ? ddmm(desde) : `${ddmm(desde)} al ${ddmm(hasta)}`}`
      : rango === "recientes"
        ? `últimos ${RECIENTES}`
        : "todo el historial";

    const q = porFecha
      ? `?desde=${desde}&hasta=${hasta}`
      : rango === "recientes"
        ? `?limite=${RECIENTES}`
        : "";
    try {
      pedidos = await api.get<PedidoDeLocal[]>(`/api/cliente/pedidos${q}`);
    } catch (e) {
      lista.innerHTML = `<div class="tarjeta text-red-700">${esc(
        e instanceof Error ? e.message : "Error",
      )}</div>`;
      return;
    }

    if (pedidos.length === 0) {
      // Vacio por el filtro no es lo mismo que no haber pedido nunca: el primer
      // caso se arregla tocando "Todo", el segundo haciendo un pedido.
      lista.innerHTML =
        rango === "todo" || rango === "recientes"
          ? `<div class="tarjeta text-center">
               <p class="mb-3 text-masa-500">Todavia no hiciste ningún pedido.</p>
               <button id="primero" class="btn-primario">Hacer un pedido</button>
             </div>`
          : `<div class="tarjeta text-center">
               <p class="mb-3 text-masa-500">No hay pedidos con entrega en este periodo.</p>
               <button id="ampliar" class="btn-sec">Ver todos</button>
             </div>`;
      lista.querySelector("#primero")?.addEventListener("click", () => navegar("#/pedir"));
      lista.querySelector("#ampliar")?.addEventListener("click", () => {
        rango = "todo";
        void cargar();
      });
      return;
    }

    lista.innerHTML = pedidos
      .map((p) => {
        const chip = CHIP[p.estado];
        return `
        <div class="tarjeta">
          <div class="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div class="font-bold">Entrega ${esc(p.fecha_entrega)} · ${esc(nombreTurno(p.turno))}</div>
              <div class="text-xs text-masa-400">${esc(p.cliente_nombre)} · pedido #${esc(p.id)} · mandado el ${esc(diaDeEnvio(p.creado_en))}</div>
            </div>
            <span class="rounded-full px-2.5 py-1 text-xs font-bold ${chip.clase}">${chip.texto}</span>
          </div>
          <div class="mt-2 flex flex-wrap items-center justify-between gap-2">
            <span class="text-lg font-bold tabular-nums">${formatearPesos(p.total)}</span>
            <span class="flex gap-2">
              <button class="btn-sec" data-ver="${p.id}">Ver detalle</button>
              ${
                p.estado === "PENDIENTE"
                  ? `<button class="btn-sec" data-editar="${p.id}">Corregir</button>
                     <button class="btn-peligro" data-cancelar="${p.id}">Cancelar</button>`
                  : ""
              }
            </span>
          </div>
        </div>`;
      })
      .join("");
  }

  async function detalle(id: number): Promise<{ lineas: LineaDeLocal[] }> {
    return api.get<{ lineas: LineaDeLocal[] }>(`/api/cliente/pedidos/${id}`);
  }

  lista.addEventListener("click", async (ev) => {
    const objetivo = ev.target as HTMLElement;

    const ver = objetivo.closest("[data-ver]");
    if (ver) {
      const id = Number(ver.getAttribute("data-ver"));
      const p = pedidos.find((x) => x.id === id);
      await abrirDetalleLocal(dlg, id, { fecha_entrega: p?.fecha_entrega, turno: p?.turno });
      return;
    }

    const editar = objetivo.closest("[data-editar]");
    if (editar) {
      const id = Number(editar.getAttribute("data-editar"));
      const p = pedidos.find((x) => x.id === id);
      const { lineas } = await detalle(id);
      editarPedidoPendiente(id, p?.cliente_id ?? 0, lineas);
      navegar("#/pedir");
      return;
    }

    const cancelar = objetivo.closest("[data-cancelar]");
    if (cancelar) {
      const id = Number(cancelar.getAttribute("data-cancelar"));
      dlg.innerHTML = `
        <div class="tarjeta w-[24rem] max-w-full border-0 shadow-none">
          <h2 class="mb-2 text-lg font-bold">Cancelar el pedido #${esc(id)}</h2>
          <p class="text-sm text-masa-600">No se produce nada de este pedido. Si después lo necesitás, hay que hacerlo de nuevo.</p>
          <p id="c_err" class="mt-2 text-sm text-red-600"></p>
          <div class="mt-4 flex justify-end gap-2">
            <button id="no" class="btn-sec">Dejarlo como está</button>
            <button id="si" class="btn-peligro">Si, cancelar</button>
          </div>
        </div>`;
      const cErr = dlg.querySelector("#c_err") as HTMLElement;
      dlg.querySelector("#no")?.addEventListener("click", () => dlg.close());
      dlg.querySelector("#si")?.addEventListener("click", async () => {
        try {
          await api.del(`/api/cliente/pedidos/${id}`);
          dlg.close();
          await cargar();
        } catch (e) {
          cErr.textContent = e instanceof Error ? e.message : "Error";
        }
      });
      dlg.showModal();
    }
  });

  contenedor.querySelector("#rangos")?.addEventListener("click", (ev) => {
    const b = (ev.target as HTMLElement).closest<HTMLElement>("[data-rango]");
    if (!b) return;
    rango = b.dataset.rango as Rango;
    void cargar();
  });

  // Cuando la confiteria confirma o rechaza, llega el push: la lista se pone
  // al dia sin que el encargado tenga que salir y volver a entrar.
  alLlegarAviso(lista, () => void cargar());

  await cargar();
}
